import {
  StandardLonghandProperties,
  StandardProperties,
  StandardShorthandProperties,
} from 'csstype';

export type BaseOmittedProperties =
  // Translate collides with the translate prop in GlobalHTMLAttributes
  // see https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/translate
  'translate';

/**
 * All css properties that can be used in the Base component to style the component
 * by passing them as props
 */
export interface BaseCSSProperties
  extends Omit<StandardProperties<string | number, string & {}>, BaseOmittedProperties> {}

export const StandardLonghandPropertiesList: (keyof StandardLonghandProperties<
  string | number,
  string & {}
>)[] = [
  'accentColor',
  'alignContent',
  'alignItems',
  'alignSelf',
  'alignTracks',
  'animationComposition',
  'animationDelay',
  'animationDirection',
  'animationDuration',
  'animationFillMode',
  'animationIterationCount',
  'animationName',
  'animationPlayState',
  'animationRangeEnd',
  'animationRangeStart',
  'animationTimeline',
  'animationTimingFunction',
  'appearance',
  'aspectRatio',
  'backdropFilter',
  'backfaceVisibility',
  'backgroundAttachment',
  'backgroundBlendMode',
  'backgroundClip',
  'backgroundColor',
  'backgroundImage',
  'backgroundOrigin',
  'backgroundPositionX',
  'backgroundPositionY',
  'backgroundRepeat',
  'backgroundSize',
  'blockOverflow',
  'blockSize',
  'borderBlockColor',
  'borderBlockEndColor',
  'borderBlockEndStyle',
  'borderBlockEndWidth',
  'borderBlockStartColor',
  'borderBlockStartStyle',
  'borderBlockStartWidth',
  'borderBlockStyle',
  'borderBlockWidth',
  'borderBottomColor',
  'borderBottomLeftRadius',
  'borderBottomRightRadius',
  'borderBottomStyle',
  'borderBottomWidth',
  'borderCollapse',
  'borderEndEndRadius',
  'borderEndStartRadius',
  'borderImageOutset',
  'borderImageRepeat',
  'borderImageSlice',
  'borderImageSource',
  'borderImageWidth',
  'borderInlineColor',
  'borderInlineEndColor',
  'borderInlineEndStyle',
  'borderInlineEndWidth',
  'borderInlineStartColor',
  'borderInlineStartStyle',
  'borderInlineStartWidth',
  'borderInlineStyle',
  'borderInlineWidth',
  'borderLeftColor',
  'borderLeftStyle',
  'borderLeftWidth',
  'borderRightColor',
  'borderRightStyle',
  'borderRightWidth',
  'borderSpacing',
  'borderStartEndRadius',
  'borderStartStartRadius',
  'borderTopColor',
  'borderTopLeftRadius',
  'borderTopRightRadius',
  'borderTopStyle',
  'borderTopWidth',
  'bottom',
  'boxDecorationBreak',
  'boxShadow',
  'boxSizing',
  'breakAfter',
  'breakBefore',
  'breakInside',
  'captionSide',
  'caretColor',
  'caretShape',
  'clear',
  'clipPath',
  'color',
  'colorAdjust',
  'colorScheme',
  'columnCount',
  'columnFill',
  'columnGap',
  'columnRuleColor',
  'columnRuleStyle',
  'columnRuleWidth',
  'columnSpan',
  'columnWidth',
  'contain',
  'containIntrinsicBlockSize',
  'containIntrinsicHeight',
  'containIntrinsicInlineSize',
  'containIntrinsicWidth',
  'containerName',
  'containerType',
  'content',
  'contentVisibility',
  'counterIncrement',
  'counterReset',
  'counterSet',
  'cursor',
  'direction',
  'display',
  'emptyCells',
  'filter',
  'flexBasis',
  'flexDirection',
  'flexGrow',
  'flexShrink',
  'flexWrap',
  'float',
  'fontFamily',
  'fontFeatureSettings',
  'fontKerning',
  'fontLanguageOverride',
  'fontOpticalSizing',
  'fontPalette',
  'fontSize',
  'fontSizeAdjust',
  'fontSmooth',
  'fontStretch',
  'fontStyle',
  'fontSynthesis',
  'fontSynthesisPosition',
  'fontSynthesisSmallCaps',
  'fontSynthesisStyle',
  'fontSynthesisWeight',
  'fontVariant',
  'fontVariantAlternates',
  'fontVariantCaps',
  'fontVariantEastAsian',
  'fontVariantEmoji',
  'fontVariantLigatures',
  'fontVariantNumeric',
  'fontVariantPosition',
  'fontVariationSettings',
  'fontWeight',
  'forcedColorAdjust',
  'gridAutoColumns',
  'gridAutoFlow',
  'gridAutoRows',
  'gridColumnEnd',
  'gridColumnStart',
  'gridRowEnd',
  'gridRowStart',
  'gridTemplateAreas',
  'gridTemplateColumns',
  'gridTemplateRows',
  'hangingPunctuation',
  'height',
  'hyphenateCharacter',
  'hyphenateLimitChars',
  'hyphens',
  'imageOrientation',
  'imageRendering',
  'imageResolution',
  'initialLetter',
  'inlineSize',
  'inputSecurity',
  'insetBlockEnd',
  'insetBlockStart',
  'insetInlineEnd',
  'insetInlineStart',
  'isolation',
  'justifyContent',
  'justifyItems',
  'justifySelf',
  'justifyTracks',
  'left',
  'letterSpacing',
  'lineBreak',
  'lineHeight',
  'lineHeightStep',
  'listStyleImage',
  'listStylePosition',
  'listStyleType',
  'marginBlockEnd',
  'marginBlockStart',
  'marginBottom',
  'marginInlineEnd',
  'marginInlineStart',
  'marginLeft',
  'marginRight',
  'marginTop',
  'marginTrim',
  'maskBorderMode',
  'maskBorderOutset',
  'maskBorderRepeat',
  'maskBorderSlice',
  'maskBorderSource',
  'maskBorderWidth',
  'maskClip',
  'maskComposite',
  'maskImage',
  'maskMode',
  'maskOrigin',
  'maskPosition',
  'maskRepeat',
  'maskSize',
  'maskType',
  'masonryAutoFlow',
  'mathDepth',
  'mathShift',
  'mathStyle',
  'maxBlockSize',
  'maxHeight',
  'maxInlineSize',
  'maxLines',
  'maxWidth',
  'minBlockSize',
  'minHeight',
  'minInlineSize',
  'minWidth',
  'mixBlendMode',
  'motionDistance',
  'motionPath',
  'motionRotation',
  'objectFit',
  'objectPosition',
  'offsetAnchor',
  'offsetDistance',
  'offsetPath',
  'offsetPosition',
  'offsetRotate',
  'offsetRotation',
  'opacity',
  'order',
  'orphans',
  'outlineColor',
  'outlineOffset',
  'outlineStyle',
  'outlineWidth',
  'overflowAnchor',
  'overflowBlock',
  'overflowClipBox',
  'overflowClipMargin',
  'overflowInline',
  'overflowWrap',
  'overflowX',
  'overflowY',
  'overlay',
  'overscrollBehaviorBlock',
  'overscrollBehaviorInline',
  'overscrollBehaviorX',
  'overscrollBehaviorY',
  'paddingBlockEnd',
  'paddingBlockStart',
  'paddingBottom',
  'paddingInlineEnd',
  'paddingInlineStart',
  'paddingLeft',
  'paddingRight',
  'paddingTop',
  'page',
  'pageBreakAfter',
  'pageBreakBefore',
  'pageBreakInside',
  'paintOrder',
  'perspective',
  'perspectiveOrigin',
  'pointerEvents',
  'position',
  'printColorAdjust',
  'quotes',
  'resize',
  'right',
  'rotate',
  'rowGap',
  'rubyAlign',
  'rubyMerge',
  'rubyPosition',
  'scale',
  'scrollBehavior',
  'scrollMarginBlockEnd',
  'scrollMarginBlockStart',
  'scrollMarginBottom',
  'scrollMarginInlineEnd',
  'scrollMarginInlineStart',
  'scrollMarginLeft',
  'scrollMarginRight',
  'scrollMarginTop',
  'scrollPaddingBlockEnd',
  'scrollPaddingBlockStart',
  'scrollPaddingBottom',
  'scrollPaddingInlineEnd',
  'scrollPaddingInlineStart',
  'scrollPaddingLeft',
  'scrollPaddingRight',
  'scrollPaddingTop',
  'scrollSnapAlign',
  'scrollSnapMarginBottom',
  'scrollSnapMarginLeft',
  'scrollSnapMarginRight',
  'scrollSnapMarginTop',
  'scrollSnapStop',
  'scrollSnapType',
  'scrollTimelineAxis',
  'scrollTimelineName',
  'scrollbarColor',
  'scrollbarGutter',
  'scrollbarWidth',
  'shapeImageThreshold',
  'shapeMargin',
  'shapeOutside',
  'tabSize',
  'tableLayout',
  'textAlign',
  'textAlignLast',
  'textCombineUpright',
  'textDecorationColor',
  'textDecorationLine',
  'textDecorationSkip',
  'textDecorationSkipInk',
  'textDecorationStyle',
  'textDecorationThickness',
  'textEmphasisColor',
  'textEmphasisPosition',
  'textEmphasisStyle',
  'textIndent',
  'textJustify',
  'textOrientation',
  'textOverflow',
  'textRendering',
  'textShadow',
  'textSizeAdjust',
  'textTransform',
  'textUnderlineOffset',
  'textUnderlinePosition',
  'textWrap',
  'timelineScope',
  'top',
  'touchAction',
  'transform',
  'transformBox',
  'transformOrigin',
  'transformStyle',
  'transitionBehavior',
  'transitionDelay',
  'transitionDuration',
  'transitionProperty',
  'transitionTimingFunction',
  'translate',
  'unicodeBidi',
  'userSelect',
  'verticalAlign',
  'viewTimelineAxis',
  'viewTimelineInset',
  'viewTimelineName',
  'viewTransitionName',
  'visibility',
  'whiteSpace',
  'whiteSpaceCollapse',
  'whiteSpaceTrim',
  'widows',
  'width',
  'willChange',
  'wordBreak',
  'wordSpacing',
  'wordWrap',
  'writingMode',
  'zIndex',
  'zoom',
] as const;

export const StandardShorthandPropertiesList: (keyof StandardShorthandProperties<
  string | number,
  string & {}
>)[] = [
  'all',
  'animation',
  'animationRange',
  'background',
  'backgroundPosition',
  'border',
  'borderBlock',
  'borderBlockEnd',
  'borderBlockStart',
  'borderBottom',
  'borderColor',
  'borderImage',
  'borderInline',
  'borderInlineEnd',
  'borderInlineStart',
  'borderLeft',
  'borderRadius',
  'borderRight',
  'borderStyle',
  'borderTop',
  'borderWidth',
  'caret',
  'columnRule',
  'columns',
  'containIntrinsicSize',
  'container',
  'flex',
  'flexFlow',
  'font',
  'gap',
  'grid',
  'gridArea',
  'gridColumn',
  'gridRow',
  'gridTemplate',
  'inset',
  'insetBlock',
  'insetInline',
  'lineClamp',
  'listStyle',
  'margin',
  'marginBlock',
  'marginInline',
  'mask',
  'maskBorder',
  'motion',
  'offset',
  'outline',
  'overflow',
  'overscrollBehavior',
  'padding',
  'paddingBlock',
  'paddingInline',
  'placeContent',
  'placeItems',
  'placeSelf',
  'scrollMargin',
  'scrollMarginBlock',
  'scrollMarginInline',
  'scrollPadding',
  'scrollPaddingBlock',
  'scrollPaddingInline',
  'scrollSnapMargin',
  'scrollTimeline',
  'textDecoration',
  'textEmphasis',
  'transition',
  'viewTimeline',
] as const;

export const StandardPropertiesList: (keyof StandardProperties<string | number, string & {}>)[] = [
  ...StandardLonghandPropertiesList,
  ...StandardShorthandPropertiesList,
] as const;
export const BaseOmittedPropertiesList: BaseOmittedProperties[] = ['translate'];
export const BaseCSSPropertiesList: (keyof BaseCSSProperties)[] = StandardPropertiesList.filter(
  (key) => !BaseOmittedPropertiesList.includes(key as any)
) as (keyof BaseCSSProperties)[];

export type BaseCssProps = PickPropKeys<BaseCSSProperties, typeof BaseCSSPropertiesList>;

/**
 * Base component props
 *
 * ```ts
 * interface YourComponentProps extends BaseExtendProps<React.HTMLAttributes<HTMLDivElement>> {
 *  // your props
 * }
 * ```
 *
 */
export type BaseExtendProps<P> = OverrideProps<BaseCssProps, P>;
